import { Button, cn } from "@win-reality/react-ui";

import { TeamRoster } from "../../types/roster";
import { canEditRoster, RosterEditTooltip } from "./RosterEditTooltip";

type LineupFooterProps = {
  /** The shown team's roster: locked rosters can't take new players */
  roster: TeamRoster;
  /** Either batting order differs from the game's, so there's a save to make */
  isChanged: boolean;
  isSaving: boolean;
  onAddPlayers: () => void;
  onSave: () => void;
};

/** Manage Lineups footer: add players to the roster, then save the lineups. */
export const LineupFooter = ({
  roster,
  isChanged,
  isSaving,
  onAddPlayers,
  onSave,
}: LineupFooterProps) => (
  <div className="space-y-3 border-t border-tagging-option-border px-6 py-4">
    <RosterEditTooltip
      roster={roster}
      className="w-full"
      data-testid="lineup-roster-locked-status"
    >
      <Button
        // The softer button beside Save Lineup. react-ui's accent colors flip
        // with the theme: "dark" is light gray on the light panel, but black
        // on the dark one, so there it takes a dark gray instead
        variant="secondary-flat-dark"
        onClick={onAddPlayers}
        isDisabled={!canEditRoster(roster)}
        data-testid="lineup-add-players-button"
        className={cn(
          // Size
          "w-full",
          // Border
          "rounded-full",
          // Background
          "dark:bg-tagging-option-selected dark:hover:bg-tagging-option-selected/80",
          // Text
          "dark:text-white",
        )}
      >
        + Add New Players
      </Button>
    </RosterEditTooltip>
    <Button
      // Black on the light panel, white on the dark one: react-ui's "light"
      // variant takes the theme's strongest color
      variant="secondary-flat-light"
      onClick={onSave}
      // Nothing to save until a batting order differs from the game's
      isDisabled={!isChanged || isSaving}
      isLoading={isSaving}
      aria-busy={isSaving}
      data-testid="lineup-save-button"
      className="w-full rounded-full"
    >
      Save Lineup
    </Button>
  </div>
);
